/**
 * What we agreed to charge, and until when.
 *
 * A rate contract never reaches the ledger. It is still controlled, because it
 * decides what every invoice raised under it will say — and the two ways that
 * goes wrong are both silent.
 *
 * **A price is superseded, never edited.** A new rate closes the old one on the
 * day before it starts, so re-printing last quarter's invoice still produces
 * the figure the customer received.
 *
 * **A discount nobody read is a discount nobody agreed to.** The person who
 * writes a contract cannot be the person who puts it into effect.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { Card, ErrorNote, Field, Loading, Note, Pill } from '../components/ui'

interface Rate {
  id: string
  service_code: string
  description: string
  rate_paise: number
  gl_account_id: string | null
  is_exempt: boolean
  gst_rate_bp: number
  effective_from: string
  effective_to: string | null
}

interface Contract {
  id: string
  reference: string
  party_id: string
  party_name: string
  kind: string
  status: string
  title: string
  starts_on: string
  ends_on: string | null
  payment_terms_days: number
  expected_deduction_bp: number
  notes: string | null
  created_by: string | null
  rates: Rate[]
}

interface Customer {
  id: string
  name: string
}

interface RevenueHead {
  id: string
  code: string
  name: string
}

const today = () => new Date().toISOString().slice(0, 10)

function toPaise(text: string): number {
  const clean = text.replace(/[^0-9.-]/g, '')
  if (!clean) return 0
  return Math.round(Number(clean) * 100)
}

export default function Contracts() {
  const { can, me } = useAuth()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState<string | null>(null)
  const [drafting, setDrafting] = useState(false)
  const [party, setParty] = useState('')
  const [kind, setKind] = useState('corporate')
  const [title, setTitle] = useState('')
  const [startsOn, setStartsOn] = useState(today())
  const [endsOn, setEndsOn] = useState('')
  const [terms, setTerms] = useState('30')
  const [deduction, setDeduction] = useState('0')

  const [serviceCode, setServiceCode] = useState('')
  const [rateName, setRateName] = useState('')
  const [rateAmount, setRateAmount] = useState('')
  const [rateHead, setRateHead] = useState('')
  const [rateExempt, setRateExempt] = useState(true)
  const [rateGst, setRateGst] = useState('0')
  const [rateFrom, setRateFrom] = useState('')

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['contracts'] })
    void queryClient.invalidateQueries({ queryKey: ['contracts-expiring'] })
  }

  const contracts = useQuery({
    queryKey: ['contracts'],
    queryFn: () => api.get<Contract[]>('/receivables/contracts'),
  })
  const expiring = useQuery({
    queryKey: ['contracts-expiring'],
    queryFn: () => api.get<Contract[]>('/receivables/contracts/expiring?days=60'),
  })
  const customers = useQuery({
    queryKey: ['ar-customers'],
    queryFn: () => api.get<Customer[]>('/receivables/customers'),
    enabled: can('ar.contract.create'),
  })
  const heads = useQuery({
    queryKey: ['revenue-heads'],
    queryFn: () => api.get<RevenueHead[]>('/receivables/revenue-heads'),
    enabled: can('ar.contract.edit'),
  })

  const create = useMutation({
    mutationFn: (body: unknown) => api.post<Contract>('/receivables/contracts', body),
    onSuccess: (made) => {
      refresh()
      setDrafting(false)
      setTitle('')
      setOpen(made.id)
    },
  })
  const addRate = useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) =>
      api.post<Contract>(`/receivables/contracts/${id}/rates`, body),
    onSuccess: () => {
      refresh()
      setServiceCode('')
      setRateName('')
      setRateAmount('')
    },
  })
  const activate = useMutation({
    mutationFn: (id: string) => api.post<Contract>(`/receivables/contracts/${id}/activate`),
    onSuccess: refresh,
  })
  const terminate = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post<Contract>(`/receivables/contracts/${id}/terminate`, { reason }),
    onSuccess: refresh,
  })

  if (contracts.isLoading) return <Loading what="Loading agreements" />
  const rows = contracts.data ?? []
  const opened = rows.find((c) => c.id === open)
  const soon = expiring.data ?? []

  return (
    <>
      <ErrorNote
        error={
          contracts.error ??
          create.error ??
          addRate.error ??
          activate.error ??
          terminate.error
        }
      />

      {soon.length > 0 && (
        <Note kind="warn">
          {soon.length === 1 ? 'One agreement runs out' : `${soon.length} agreements run out`}{' '}
          within sixty days: {soon.map((c) => `${c.party_name} (${c.ends_on})`).join(', ')}.
          A tie-up that lapses unnoticed is billed at list price, and the first anybody
          hears of it is the complaint.
        </Note>
      )}

      {can('ar.contract.create') && (
        <Card
          title="Agree a new contract"
          actions={
            <button className="small" onClick={() => setDrafting(!drafting)}>
              {drafting ? 'Cancel' : 'New contract'}
            </button>
          }
        >
          {!drafting ? (
            <p className="faint">
              A contract proposes; an invoice decides. The rate fills a line in, and the
              line keeps its own amount.
            </p>
          ) : (
            <>
              <div className="row">
                <Field label="Customer">
                  <select value={party} onChange={(e) => setParty(e.target.value)}>
                    <option value="">Choose…</option>
                    {(customers.data ?? []).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Kind">
                  <select value={kind} onChange={(e) => setKind(e.target.value)}>
                    <option value="corporate">Corporate tie-up</option>
                    <option value="insurer">Insurer panel</option>
                    <option value="supplier">Supplier</option>
                  </select>
                </Field>
                <Field label="Title">
                  <input value={title} onChange={(e) => setTitle(e.target.value)} />
                </Field>
              </div>
              <div className="row">
                <Field label="Starts">
                  <input
                    type="date"
                    value={startsOn}
                    onChange={(e) => setStartsOn(e.target.value)}
                  />
                </Field>
                <Field label="Ends" caption="blank means open-ended">
                  <input
                    type="date"
                    value={endsOn}
                    onChange={(e) => setEndsOn(e.target.value)}
                  />
                </Field>
                <Field label="Payment terms" caption="days">
                  <input
                    className="num"
                    value={terms}
                    onChange={(e) => setTerms(e.target.value)}
                  />
                </Field>
                <Field
                  label="Expected deduction"
                  caption="basis points an insurer knocks off"
                >
                  <input
                    className="num"
                    value={deduction}
                    onChange={(e) => setDeduction(e.target.value)}
                  />
                </Field>
              </div>
              <button
                disabled={!party || !title}
                onClick={() =>
                  create.mutate({
                    party_id: party,
                    kind,
                    title,
                    starts_on: startsOn,
                    ends_on: endsOn || null,
                    payment_terms_days: Number(terms || '30'),
                    expected_deduction_bp: Number(deduction || '0'),
                  })
                }
              >
                Save as draft
              </button>
              <p className="faint">
                It quotes nothing until somebody else activates it. Billing from a draft
                would be billing a price nobody signed off.
              </p>
            </>
          )}
        </Card>
      )}

      <Card title="Agreements" hint={`${rows.length}`} tight>
        {rows.length === 0 ? (
          <div className="empty">Nothing agreed yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Customer</th>
                <th>Kind</th>
                <th>Runs</th>
                <th>Status</th>
                <th className="num">Rates</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((contract) => (
                <tr key={contract.id}>
                  <td>
                    <button className="small" onClick={() => setOpen(contract.id)}>
                      {contract.reference}
                    </button>
                    <div className="faint">{contract.title}</div>
                  </td>
                  <td>{contract.party_name}</td>
                  <td className="muted">{contract.kind}</td>
                  <td className="muted">
                    {contract.starts_on} → {contract.ends_on ?? 'open'}
                  </td>
                  <td>
                    <Pill value={contract.status} />
                  </td>
                  <td className="num">{contract.rates.length}</td>
                  <td>
                    {contract.status === 'draft' && can('ar.contract.approve') && (
                      <button
                        className="small"
                        disabled={
                          contract.rates.length === 0 || contract.created_by === me?.user_id
                        }
                        title={
                          contract.created_by === me?.user_id
                            ? 'You wrote this, so somebody else has to agree to it.'
                            : contract.rates.length === 0
                              ? 'It has no rates, so activating it would agree to nothing.'
                              : undefined
                        }
                        onClick={() => activate.mutate(contract.id)}
                      >
                        Activate
                      </button>
                    )}
                    {contract.status === 'draft' && !can('ar.contract.approve') && (
                      <span className="faint">waiting on a checker</span>
                    )}
                    {contract.status === 'active' && can('ar.contract.approve') && (
                      <button
                        className="small"
                        onClick={() => {
                          const reason = window.prompt('Why is this ending?')
                          if (reason) terminate.mutate({ id: contract.id, reason })
                        }}
                      >
                        Terminate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {opened && (
        <Card
          title={`${opened.reference} — agreed prices`}
          hint={opened.party_name}
          actions={
            <button className="small" onClick={() => setOpen(null)}>
              Close
            </button>
          }
          tight
        >
          {opened.rates.length === 0 ? (
            <div className="empty">No prices agreed yet.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Description</th>
                  <th>Tax</th>
                  <th>From</th>
                  <th>Until</th>
                  <th className="num">Rate</th>
                </tr>
              </thead>
              <tbody>
                {opened.rates.map((rate) => (
                  <tr key={rate.id}>
                    <td className="mono">{rate.service_code}</td>
                    <td>{rate.description}</td>
                    <td className="muted">
                      {rate.is_exempt ? 'exempt' : `${rate.gst_rate_bp / 100}%`}
                    </td>
                    <td className="muted">{rate.effective_from}</td>
                    <td className="muted">
                      {rate.effective_to ?? <span className="faint">current</span>}
                    </td>
                    <td className="num">{formatInr(rate.rate_paise)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {can('ar.contract.edit') && (
            <>
              <div className="row">
                <Field label="Service code">
                  <input
                    value={serviceCode}
                    placeholder="CONSULT"
                    onChange={(e) => setServiceCode(e.target.value)}
                  />
                </Field>
                <Field label="Description">
                  <input value={rateName} onChange={(e) => setRateName(e.target.value)} />
                </Field>
                <Field label="Revenue head">
                  <select value={rateHead} onChange={(e) => setRateHead(e.target.value)}>
                    <option value="">Choose…</option>
                    {(heads.data ?? []).map((h) => (
                      <option key={h.id} value={h.id}>
                        {h.code} {h.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="GST">
                  <select
                    value={rateExempt ? 'exempt' : rateGst}
                    onChange={(e) => {
                      if (e.target.value === 'exempt') {
                        setRateExempt(true)
                        setRateGst('0')
                      } else {
                        setRateExempt(false)
                        setRateGst(e.target.value)
                      }
                    }}
                  >
                    <option value="exempt">Exempt</option>
                    <option value="500">5%</option>
                    <option value="1200">12%</option>
                    <option value="1800">18%</option>
                  </select>
                </Field>
                <Field label="Rate">
                  <input
                    className="num"
                    value={rateAmount}
                    onChange={(e) => setRateAmount(e.target.value)}
                  />
                </Field>
                <Field label="Effective from" caption="blank means the contract's start">
                  <input
                    type="date"
                    value={rateFrom}
                    onChange={(e) => setRateFrom(e.target.value)}
                  />
                </Field>
              </div>
              <button
                disabled={!serviceCode || !rateName || !toPaise(rateAmount)}
                onClick={() =>
                  addRate.mutate({
                    id: opened.id,
                    body: {
                      service_code: serviceCode,
                      description: rateName,
                      rate_paise: toPaise(rateAmount),
                      gl_account_id: rateHead || null,
                      is_exempt: rateExempt,
                      gst_rate_bp: rateExempt ? 0 : Number(rateGst),
                      effective_from: rateFrom || null,
                    },
                  })
                }
              >
                Agree this price
              </button>
              <p className="faint">
                A price for a service that already has one closes the old row on the day
                before this starts. Nothing is edited, because the old row is why an
                invoice raised last quarter says what it says.
              </p>
            </>
          )}
        </Card>
      )}
    </>
  )
}
